import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * A "back to the list" control that really goes BACK.
 *
 * The companion to `useUrlViewState`, and the half of the problem it cannot
 * solve on its own. The URL carries a list's filters across the trip to a
 * detail screen; what carries the SCROLL is the history entry itself, and
 * only a POP restores that (see `Components/ScrollMemory`). So a back
 * control rendered as a plain link to the list — which is what the
 * operator's trip screen was — PUSHES a new entry instead: same path, no
 * query string, scrolled to the top. It looks like Back and undoes exactly
 * what Back is for.
 *
 * `navigate(-1)` is the fix, with one hole: a screen reached directly — a
 * reload, a pasted URL, a link out of a WhatsApp message — has no entry of
 * ours behind it, and -1 then either does nothing at all (the driver's trip
 * screen, where the back arrow was simply dead after a reload) or walks the
 * reader out of the app. React Router keys that first entry 'default', in
 * the browser and in MemoryRouter alike, which is the test used here.
 *
 * `fallback` is where such a visit goes instead, as a REPLACE: there is
 * nothing worth keeping behind a screen someone arrived at cold, and
 * replacing it means Back from the list still leaves rather than bouncing
 * into the detail screen again.
 */
export default function useBackTo(fallback) {
  const navigate = useNavigate();
  const { key } = useLocation();
  const canGoBack = key !== 'default';

  return useCallback(() => {
    if (canGoBack) navigate(-1);
    else navigate(fallback, { replace: true });
  }, [canGoBack, fallback, navigate]);
}

/**
 * True when a click on a link should be left to the browser — a new tab, a
 * new window, a download. React Router's own Link makes the same test; ours
 * has to repeat it because our handler runs FIRST, and calling
 * preventDefault on a cmd-click would take "open in a new tab" away.
 */
export const isPlainClick = (event) => !event.metaKey && !event.ctrlKey
  && !event.shiftKey && !event.altKey;
